import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useCurrentUser } from '@/hooks/api/use-auth';
import { storage } from '@/lib/storage';
import type { User } from '@/api/types/auth.types';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  token: string | null;
  refreshAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(storage.getToken());
  const [user, setUser] = useState<User | null>(storage.getUser<User>());
  const [refreshKey, setRefreshKey] = useState(0);

  const { data: currentUser, isLoading } = useCurrentUser();

  useEffect(() => {
    if (currentUser) {
      setUser(currentUser);
      storage.setUser<User>(currentUser);
    }
  }, [currentUser]);

  useEffect(() => {
    const storedToken = storage.getToken();
    setToken(storedToken);
    
    if (!storedToken) {
      setUser(null);
      storage.removeUser();
    }
  }, [refreshKey]);

  const refreshAuth = () => {
    setRefreshKey(prev => prev + 1);
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!token && !!user,
    token,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
